import { FilterConfigReport, Filter, FilterType } from '../filter/filter.model';
import { IOrgLookupType, DateMonthModes, IMetricType } from '../enums/enums';
import { IKeyValuePair, INameValuePair, IPreviousDateRangeDisplay, IRole } from './models';
import { KpiSheetColumn } from '../components/kpi-sheet/kpi-sheet-models';
import { Observable } from 'rxjs';
import { Options } from 'highcharts';
import { Element } from './report-view.models';

export interface ICardHeaderConfig {
  title: string;
  iconClass?: string;
  helpTextKey?: string;
  helpTextTitle?: string;
  exportName?: string;
  metricDisplayModes?: string[];
  availableMetrics?: IMetricConfig [];
  defaultMetricId?: number;
  codeSlicers?: INameValuePair[];
}


export interface IDefaultTableColumnConfig {
  show: boolean;
  print?: boolean;
  header: string;
  columnDef: string;
  columnClasses?: string[]
  hasComparisonMetrics?: boolean;
  isMom?: boolean;
  isYoy?: boolean;
  isLookback?: boolean;
  metricFormatKey: string;
  printFormatKey?: string;
  comparisonFormatKey?: string;
  clickable?: boolean;
  sortable?: boolean;
  valueInverted?: boolean;
}

export interface IMultiLevelDualAxisLineGraphConfig {
  xAxisPropertyName: string;
  parentMetrics: IMultiLevelTrendGraphMetricPropertyConfig[];
  childMetrics: IMultiLevelTrendGraphMetricPropertyConfig[];
  defaultParentIds: number[];
  defaultChildrenIds: number[] | string[];
  metricOneColorHexCode: string;
  metricTwoColorHexCode: string;
}

export interface IDualAxisLineGraphConfig {
  xAxisPropertyName: string;
  availableMetrics: ITrendGraphMetricPropertyConfig[];
  defaultMetricOneId: number;
  defaultMetricTwoId: number;
  metricOneColorHexCode: string;
  metricTwoColorHexCode: string;
}

export interface IFilterConfig {
  NUMBER_OF_MONTHS_TO_SHOW_DEFAULT: number;
  NUMBER_OF_QUARTERS_TO_SHOW_DEFAULT: number;
  defaultMonthDateMode?: DateMonthModes;
  hideCalendarToggle?: boolean;
  showEntireOrgHierarchyForAllUsers: boolean;
  onlyShowDealersInFilter?: boolean;
  filterReportConfigs: { [id: string]: FilterConfigReport };
  filterTypeLookup: { [id: string]: FilterType };
  FILTER_CONFIG: { [id: string]: Filter };
}

export interface IHelpTextConfig {

}

export interface IInventoryOverviewConfig {
  billboards: IInventoryOverviewBillboardConfig[];
  inventorySummaryColumns: IDefaultTableColumnConfig[];
  classSummaryColumns: IDefaultTableColumnConfig[];
  modelSummaryColumns: IDefaultTableColumnConfig[];
  inventoryKpiCardConfig: IInventoryOverviewKpiCardConfig;
}

export interface IInventoryOverviewKpiCardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentMonth: string;
  metricPreviousMonth: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface IInventoryOverviewBillboardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricPropertyName: string;
  metricPropertyId: number;
  metricPreviousMonthPropertyName: string;
  metricMOMPropertyName: string;
  metricFormatKey: string;
  footerLabel: string;
  footerMetricFormatKey: string;
  graphMetricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface IKpiMetricConfig {
  displayName: string;
  propertyName: string;
  yAxisTitle: string;
  pointLabelFormat: string;
  metricFormatKey: string;
  formatter: (val: any) => {};
}

export interface ILeadsFacebookConfig {
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
}

export interface ILeadsOverviewKpiConfig {
  metrics: { [id: string]: IKpiMetricConfig };
}

export interface ILeadsOverviewDealerKpiConfig {
  metrics: { [id: string]: IKpiMetricConfig };
}

export interface ILeadsOverviewBillboardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousYearPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  showTrendArrow: boolean;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface ILeadsOverviewSourceTypeCardConfig {
  showComparisonMetrics: boolean;
  leadMetric: INameValuePair;
  closeRateMetric: INameValuePair;
}

export interface ILeadsOverviewConfig {
  reportTitle?: string;
  benchmarkTitle?: string;
  metricDisplayModes: INameValuePair[];
  useDefaultComparisonColumns: boolean;
  billboards: ILeadsOverviewBillboardConfig[];
  dealerbillboards?: ILeadsOverviewBillboardConfig[];
  dealerBenchmarkColumns: IDefaultTableColumnConfig[];
  dealerBenchmarkLookbackColumns?: IDefaultTableColumnConfig[];
  dealerKpiCardConfig: ILeadsOverviewDealerKpiConfig;
  kpiCardConfig: ILeadsOverviewKpiConfig;
  lookbackMonths?: number;
  sourceTypeCardConfig: ILeadsOverviewSourceTypeCardConfig;
  dealerLevelMetricOverrides?: IPropertyOverride[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ILeadsSummaryConfig {
  reportTitle?: string;
  useDefaultComparisonColumns?: boolean;
  metricDisplayModes: INameValuePair[];
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  dealerLevelMetricOverrides?: IPropertyOverride[];
  trendTableMetrics: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ILeadsEventConfig {
  graphConfig: IDualAxisLineGraphConfig;
  trendTableMetricKeys: string[];
}

export interface ILeadTransactionsConfig {
  reportTitle?: string;
  dealerLabel?: string;
  maskSameDealerSales: boolean;
  summaryTableColumns: IDefaultTableColumnConfig[];
  excludeFromLeadDetailsTemplate?: string[];
  roleIdsWithExportPermission?: number[];
  useSourceMask?: boolean;
}

export interface ILeadsVehicleConfig {
  metricDisplayModes: INameValuePair[];
  reportTitle?: string;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  useDefaultComparisonColumns: boolean;
  dealerLevelMetricOverrides?: IPropertyOverride[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IMetricSimpleSelectConfig {
  defaultMetricId: number;
  options: ITrendGraphMetricPropertyConfig[];
}

export interface IMetricSelectConfig {
  defaultMetricId: number;
  options: IMetricConfig[];
}

export interface IMetricConfig {
  id: number;
  displayName: string;
  propertyName: string;
  metricFormatKey: string;
}

export interface IMetricComplexSelectConfig {
  defaultParentId: number;
  defaultChildId: string | number;
  parentOptions: IMultiLevelTrendGraphMetricPropertyConfig[];
  childOptions: IMultiLevelTrendGraphMetricPropertyConfig[];
}

export interface IOrgConfig {
  orgLookupTypes: Record<string, IOrgLookupType>;
  orgEntityType: Map<string, string>;
  entityTypeName: Map<string, string>;
  nationalOrgLookupTypeIds: number[];
  lmaOrgLookupTypeIds: number[];
  virtual20OrgLookupTypeIds: number[];
  dealerGroupOrgLookupTypeIds: number[];
  showUserBrand?: string[];
  availableLocales?: string[];
  nationalOrgEntityName?: string;
  mysteryShopUrlOrgs?: any;
}

export interface IPropertyOverride {
  baseProperty: string;
  overrideProperty: string;
}

export interface IRoutingConfig {
  routes: IRouteConfig[];
}

export interface IRouteConfig {
  id: string;
  category: string;
  iconClass: string;
  route?: string; // TODO: make required
  displayName: string;
  displayInNav: boolean;
  default?: boolean;
  defaultRoute?: string; // TODO: remove since we derive from children
  roles?: string[];
  order?: number;
  children: IRouteDetails[];
  disableForDealerGroups?: boolean;
  externalTool?: boolean;
  routeData?: any;
  newTab?: boolean;
  isStatic?: boolean; // Indicates this is a static route handled by Angular routing, not dynamic routing
}

export interface IRouteDetails {
  id?: string;
  parent?: string; // TODO: maybe remove or make reference to parent?
  route: string;
  displayName: string;
  displayInNav?: boolean;
  iconClass?: string; // probably never going to need this but just in case
  roles?: string[];
  pdfEnabled?: boolean;
  breadcrumbHelpEnabled?: boolean;
  showMetricSelector?: boolean;
  /**
   * Optional report identifier used for dynamic report loading
   */
  reportName?: string;
  routeData?: any;
  children?: IRouteDetails[];
  default?: boolean;
}

// Digital Advertising

export interface IDigAdOverviewChannelCardConfig {
  channelColors: string[];
  dimensionPropertyName: string;
  primaryMetricPropertyName: string;
  secondaryMetricPropertyName: string;
}

export interface IDigAdOverviewTrafficTypeChartConfig {
  colors: string[];
  paidPropertyName: string;
  organicPropertyName: string;
}

export interface IDigAdOverviewTrafficTypeCardConfig {
  headerConfig: ICardHeaderConfig;
  chartConfig: IDigAdOverviewTrafficTypeChartConfig
}

export interface IDigAdOverviewConfig {
  reportTitle: string;
  dealerLabel?: string;
  chartSeriesColors: string[];
  billboards: IDigAdOverviewBillboardConfig[];
  dealerBenchmarkColumns: IDefaultTableColumnConfig[];
  dealerBenchmarkLookbackColumns?: IDefaultTableColumnConfig[];
  kpiCardConfig: IDigAdOverviewKpiConfig;
  kpiSheetOptions: Record<KpiSheetOptions, KpiSheetColumn[]>;
  channelCardConfig: IDigAdOverviewChannelCardConfig;
  trafficTypeCardConfig: IDigAdOverviewTrafficTypeCardConfig;
  metricDisplayModes?: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IDigAdTrafficTrendKpiConfig {
  displayName: string;
  propertyName: string;
  yAxisTitle: string;
  pointLabelFormat: string;
  metricFormatKey: string;
  formatter: (val: any) => {};
}

export interface IDigAdOverviewKpiConfig {
  chartTitle: string;
  preCutoffChartTitle?: string;
  xAxisPropertyName: string;
  metrics: { [id: string]: IDigAdTrafficTrendKpiConfig };
}

export interface IDigAdOverviewBillboardConfig {
  title: string;
  subtitle: string;
  subtitleOne: string;
  subtitleTwo: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousYearPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  showTrendArrow: boolean;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface IDigAdScorecardConfig {
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
  reportTitle: string;
  scorecards: IDigAdScorecardCategoryConfig[];
}

export interface IDigAdScorecardCategoryConfig {
  category: string;
  billboards: IDigAdScorecardBillboardConfig[];
}

export interface IDigAdScorecardBillboardConfig {
  title: string;
  subtitle: string;
  subtitleMetricPropertyName?: string;
  subtitleMetricFormatKey?: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousYearPropertyName: string;
  secondaryMetricPropertyName?: string;
  secondaryMetricLabel?: string;
  secondaryMetricFormatKey?: string;
  footerLeftLabel: string;
  footerRightLabel: string;
  footerMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
  scoreCardCategory: string;
}

export interface IDigAdSummaryConfig {
  useDefaultComparisonColumns?: boolean;
  reportTitle: string;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  trendTableMetrics: INameValuePair[];
  metricDisplayModes?: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IDigAdProviderConfig {
  useDefaultComparisonColumns?: boolean;
  reportTitle: string;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  metricDisplayModes?: INameValuePair[];
}

export interface IDigAdChannelConfig {
  useDefaultComparisonColumns?: boolean;
  reportTitle: string;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  metricDisplayModes?: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IDigAdProviderScorecardConfig {
  reportTitle: string;
  scorecardMetrics: IDigAdProviderScorecardBillboardConfig[];
}

export interface IDigAdProviderScorecardBillboardConfig {
  metric: IMetricType;
  metricFormatKey: string;
}

// provider tools configs
// digital retailing configs
export type KpiSheetOptions = 'overview'; // | 'Volumes' | 'Ratios';

export interface IDigitalRetailingInStoreConfig{
  reportTitle: string;
  useDefaultComparisonColumns: boolean;
  trendGraphConfig: IDualAxisLineGraphConfig;
  metricDisplayModes: INameValuePair[];
  summaryTableColumns: IDefaultTableColumnConfig[];
  trendTableMetrics?: INameValuePair[];
  pageCopy?: string;
}

export interface IDigitalRetailingOverviewConfig {
  reportTitle: string;
  useDefaultComparisonColumns: boolean;
  showEngagementAnalysis: boolean;
  chartSeriesColors: string[];
  dealerTableLabel: string;
  billboards: IDigitalRetailingOverviewBillboardConfig[];
  kpiSheetOptions: Record<KpiSheetOptions, KpiSheetColumn[]>;
  trendGraphConfig: IMultiLevelDualAxisLineGraphConfig;
  metricDisplayModes: INameValuePair[];
  dealerTableColumns: IDefaultTableColumnConfig[];
  useV5: boolean;
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IDigitalRetailingOverviewBillboardConfig {
  title: string;
  subtitle: string;
  subtitleOne?: string;
  subtitleTwo?: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  metricPreviousYearPropertyName: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  showTrendArrow: boolean;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}


export interface IDigitalRetailingOverviewKpiConfig {
  chartTitle: string;
  preCutoffChartTitle?: string;
  xAxisPropertyName: string;
  metrics: ISalesTrendKpiConfig[];
}


export interface IDigitalRetailingTrendKpiConfig {
  displayName: string;
  propertyName: string;
  yAxisTitle: string;
  pointLabelFormat: string;
  metricFormatKey: string;
  formatter: (val: any) => {};
}


// chat configs
export interface IChatOverviewConfig {
  reportTitle: string;
  providerBreakoutAccess?: string[];
  kpiSheetOptions: any;
  billboards: IChatOverviewBillboardConfig[];
  benchmarkCardHeader: ICardHeaderConfig;
  trendGraphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  hasChatHelpText: boolean;
  dealerSummaryLabel?: string;
  metricDisplayModes: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IChatOverviewBillboardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  metricPreviousYearPropertyName: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  showTrendArrow: boolean;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface IChatSummaryConfig {
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
}

export interface IChatProviderScorecardConfig {
    reportTitle: string;
    scorecardMetrics: IChatProviderScorecardBillboardConfig[];
}

export interface IChatProviderScorecardBillboardConfig {
  metric: IMetricType;
  metricPosition: number;
  filterPosition: number;
  metricFormatKey: string;
}

// reputation management configs
export interface IReputationManagementOverviewConfig {
  reportTitle: string;
  useDefaultComparisonColumns: boolean;
  kpiSheetOptions: any;
  billboards: IReputationManagementOverviewBillboardConfig[];
  benchmarkCardHeader: ICardHeaderConfig;
  trendGraphConfig: IDualAxisLineGraphConfig;
  dealerTableColumns: IDefaultTableColumnConfig[];
}

export interface IReputationManagementOverviewBillboardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  metricPreviousYearPropertyName: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
  showTrendArrow: boolean;
}

// social media configs
export interface ISocialMediaOverviewConfig {
  reportTitle: string;
  useDefaultComparisonColumns: boolean;
  kpiSheetOptions: any;
  billboards: ISocialMediaOverviewBillboardConfig[];
  benchmarkCardHeader: ICardHeaderConfig;
  trendGraphConfig: IDualAxisLineGraphConfig;
  dealerTableColumns: IDefaultTableColumnConfig[];
}

export interface ISocialMediaOverviewBillboardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  metricPreviousYearPropertyName: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
  showTrendArrow: boolean;
}

export interface IRoleConfig {
  systemAdministratorRoles: IRole[];
  corporateRoles: IRole[];
  org5Roles: IRole[];
  org4Roles: IRole[];
  org3Roles: IRole[];
  org2Roles: IRole[];
  org1Roles: IRole[];
  dealerRoles: IRole[];
  dealerGroupRoles: IRole[];
  agencyRoles?: IRole[];
}

// trade in configs
export interface ITradeInOverviewConfig {
  reportTitle: string;
  chartSeriesColors: string[];
  billboards: ITradeInOverviewBillboardConfig[];
  kpiSheetOptions: Record<KpiSheetOptions, KpiSheetColumn[]>;
  trendGraphConfig: IMultiLevelDualAxisLineGraphConfig;
  dealerTableColumns: IDefaultTableColumnConfig[];
  tradeInKpiSheetOptions?: Record<KpiSheetOptions, KpiSheetColumn[]>;
  tradeInVehicleMakeCodes?: string[]
  dealerSummaryLabel?: string;
  metricDisplayModes: INameValuePair[];
}

export interface ITradeInOverviewBillboardConfig {
  title: string;
  subtitle: string;
  subtitleOne?: string;
  subtitleTwo?: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  metricPreviousYearPropertyName: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
  showTrendArrow?: boolean;
}


export interface ITradeInOverviewKpiConfig {
  chartTitle: string;
  preCutoffChartTitle?: string;
  xAxisPropertyName: string;
  metrics: ITradeInTrendKpiConfig[];
}

export interface ITradeInTrendKpiConfig {
  displayName: string;
  propertyName: string;
  yAxisTitle: string;
  pointLabelFormat: string;
  metricFormatKey: string;
  formatter: (val: any) => {};
}


export interface ITradeInProviderScorecardConfig {
  reportTitle: string;
  scorecardMetrics: ITradeInProviderScorecardBillboardConfig[];
}

export interface ITradeInProviderScorecardBillboardConfig {
  metric: IMetricType;
  metricPosition: number;
  filterPosition: number;
  metricFormatKey: string;
}

// sales configs

export interface ISalesForecastingConfig {
  reportTitle?: string;
  useDefaultComparisonColumns?: boolean;
  chartSeriesColors: string[];
  billboards: ISalesForecastingBillboardConfig[];
  billboardsDealerLevel: ISalesForecastingBillboardConfig[];
  dealerBenchmarkColumns: IDefaultTableColumnConfig[];
  modelPerformanceBarMetricName: string;
  modelPerformanceMetrics: ISalesForecastingModelPerformanceConfig[];
}

export interface ISalesForecastingBillboardConfig {
  metricCurrentPropertyName: string;
  metricPreviousMtdPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  trendArrowInverted: boolean;
  title: string,
  iconClass: string,
  metricTrendValuePropertyName?: number,
  subtitle1ValuePropertyName?: string,
  subtitle2ValuePropertyName?: string,
  subtitle3ValuePropertyName?: string,
  footerOneLeftValuePropertyName?: string,
  footerOneLeftFormatterKey?: string;
  footerOneLeftLabel?: string,
  footerOneLeftColor?: string,
  footerOneRightValuePropertyName?: string,
  footerOneRightFormatterKey?: string;
  footerOneRightLabel?: string,
  footerOneRightColor?: string,
  footerTwoLeftValuePropertyName?: string,
  footerTwoLeftFormatterKey?: string;
  footerTwoLeftLabel?: string,
  footerTwoLeftColor?: string,
  footerTwoRightValuePropertyName?: string,
  footerTwoRightFormatterKey?: string;
  footerTwoRightLabel?: string,
  footerTwoRightColor?: string,
  helpTextKey: string,
  helpTextTitle: string,
  chartConfig?: Observable<Highcharts.Options>,
  topPropertyName: string,
  topPropertyLabel: string,
  middlePropertyName: string,
  middlePropertyLabel: string,
  bottomPropertyName: string,
  bottomPropertyLabel: string
}

export interface ISalesForecastingModelPerformanceConfig {
  displayName: string;
  metricName: string;
  metricFormatKey: string;
  includeForCurrentMonth: boolean;
  includeForHistoricalMonth: boolean;
}

export interface ISalesOverviewConfig {
  reportTitle?: string;
  benchmarkTitle?: string;
  useDefaultComparisonColumns?: boolean;
  chartSeriesColors: string[];
  billboards: ISalesOverviewBillboardConfig[];
  dealerBenchmarkColumns: IDefaultTableColumnConfig[];
  kpiDailyCardConfig: ISalesOverviewKpiConfig;
  kpiMonthlyCardConfig: ISalesOverviewKpiConfig;
  metricDisplayModes?: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ISalesOverviewBillboardConfig {
  title: string;
  subtitle: string;
  subtitleOne: string;
  subtitleTwo: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousYearPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  showTrendArrow: boolean;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface ISalesOverviewKpiConfig {
  chartTitle: string;
  preCutoffChartTitle?: string;
  xAxisPropertyName: string;
  metrics: ISalesTrendKpiConfig[];
}

export interface ISalesTrendKpiConfig {
  displayName: string;
  propertyName: string;
  saleTypeId: number;
  yAxisTitle: string;
  pointLabelFormat: string;
  metricFormatKey: string;
  formatter: (val: any) => {};
}

export interface ISalesSourceConfig {
  reportTitle?: string;
  useDefaultComparisonColumns: boolean;
  metricDisplayModes: INameValuePair[];
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ISalesSummaryConfig {
  reportTitle?: string;
  metricDisplayModes: INameValuePair[];
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  trendTableMetrics?: INameValuePair[];
  hasTrendTable?: boolean;
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ISalesVehicleConfig {
  reportTitle?: string;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[]
  metricDisplayModes?: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ISelectConfig {
  placeholder?: string;
  default: IKeyValuePair;
  options: IKeyValuePair[];
}

export interface ISourceSummaryConfig {
  reportTitle?: string;
  metricDisplayModes: INameValuePair[];
  useDefaultComparisonColumns: boolean;
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  dealerLevelMetricOverrides?: IPropertyOverride[];
  hasTrendTable?: boolean;
  trendTableMetricKeys?: string[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface ITrendGraphConfig {
  xAxisPropertyName: string;
  availableMetrics: ITrendGraphMetricPropertyConfig[];
}

export interface ITrendGraphMetricPropertyConfig {
  id: number;
  displayName: string;
  propertyName: string;
  metricFormatKey: string;
}

export interface IMultiLevelTrendGraphConfig {
  xAxisPropertyName: string;
  availableMetrics: IMultiLevelTrendGraphMetricPropertyConfig[];
}

export interface IMultiLevelTrendGraphMetricSelectedPropertyConfig {
  parentId: number;
  childId: number;
  parentDisplayName: string;
  childDisplayName: string;
  propertyName: string;
  metricFormatKey: string;
}

export interface IMultiLevelTrendGraphMetricPropertyConfig {
  id: number;
  displayName: string;
  propertyName: string;
  metricFormatKey: string;
}

export interface IUserActivityOverviewConfig {
  reportTitle: string;
  useDefaultComparisonColumns: boolean;
  summaryTableColumns: IDefaultTableColumnConfig[];
  billboards: BillboardConfigMap[];
  // trendGraphConfig: IMultiLevelDualAxisLineGraphConfig;
  metricOneColorHexCode: string;
  metricTwoColorHexCode: string;
  summaryTableConfig: IAnalysisVisualizationHeaderConfig;
}

export interface BillboardConfigMap {
  title: string,
  iconClass: string,
  metricDataSet?: string,
  metricPropertyName: string,
  metricTrendArrowPropertyName?: string,
  metricMOMPropertyName?: string,
  metricYOYPropertyName?: string,
  metricPreviousMonthPropertyName?: string,
  metricPreviousMTDPropertyName?: string,
  metricPreviousYearPropertyName?: string,
  useFooterRowOne: boolean,
  useFooterRowTwo?: boolean,
  footerTwoLeftLabel?: string,
  footerTwoRightLabel?: string,
  footerTwoMetricFormatKey: string,
  metricFormatKey: string,
  helpTextKey: string,
  helpTextTitle: string,
  chartConfig?: CategoryChartConfigMap
}

export interface CategoryChartConfigMap {
  chartType: 'pie' | 'bar' | 'line',
  metricType: 'CloseRate' | 'InStoreConversion' | 'InStoreConversionRate' | 'ResponseRate30' | 'CustomersRepliedRate' | 'Leads',
  metricFormatKey?: string,
  dataSet: string,
  categoryPropertyName?: string,
  seriesPropertyName?: string,
  brandLabel?: string,
  districLabel?: string,
  dealerGroupLabel?: string,
  showLabels?: boolean;
}

export interface IWebsiteAfterSalesConfig {
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
}

export interface IWebsiteCertifiedChatConfig {
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
}

export interface IWebsiteLandingPageConfig {
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
}

export interface IWebsiteOverviewBillboardConfig {
  title: string;
  subtitle: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMonthDisplayOnCurrentMonth: boolean;
  metricPreviousMonthDisplayHistorical: boolean;
  metricPreviousMonthLabel: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousMTDDisplayOnCurrentMonth: boolean;
  metricPreviousMTDDisplayHistorical: boolean;
  metricPreviousMTDLabel: string;
  metricPreviousYearPropertyName: string;
  footerRowCount: number;
  footerTwoLeftLabel: string;
  footerTwoRightLabel: string;
  footerTwoMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  showTrendArrow: boolean;
  metricFormatKey: string;
  secondaryMetricCard?: boolean;
  secondaryMetricLabel?: string;
  secondaryMetricPropertyName?: string;
  secondaryMetricFormatKey?: string;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface IWebsiteScorecardBillboardConfig {
  title: string;
  subtitle: string;
  subtitleMetricPropertyName?: string;
  subtitleMetricFormatKey?: string;
  iconClass: string;
  reverseMetric: boolean;
  metricCurrentPropertyName: string;
  metricMOMPropertyName: string;
  metricYOYPropertyName: string;
  metricPreviousMonthPropertyName: string;
  metricPreviousMTDPropertyName: string;
  metricPreviousYearPropertyName: string;
  secondaryMetricPropertyName?: string;
  secondaryMetricLabel?: string;
  secondaryMetricFormatKey?: string;
  footerLeftLabel: string;
  footerRightLabel: string;
  footerMetricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  metricFormatKey: string;
  helpTextKey: string;
  helpTextTitle: string;
  scoreCardCategory: string;
}

export interface IWebsiteScorecardCategoryConfig {
  category: string;
  billboards: IWebsiteScorecardBillboardConfig[];
}

export interface IWebsiteVisitorTrendKpiConfig {
  displayName: string;
  propertyName: string;
  yAxisTitle: string;
  pointLabelFormat: string;
  metricFormatKey: string;
  formatter: (val: any) => {};
}

export interface IWebsiteOverviewKpiConfig {
  chartTitle: string;
  preCutoffChartTitle?: string;
  showRateMetric?: boolean;
  rateMetricDisplayName?: string;
  xAxisPropertyName: string;
  metrics: { [id: string]: IWebsiteVisitorTrendKpiConfig };
}

export interface IWebsiteOverviewConfig {
  reportTitle: string;
  useNewMetricCutoffDate: boolean;
  useDefaultComparisonColumns: boolean;
  newMetricCutoffDate?: Date;
  billboards: IWebsiteOverviewBillboardConfig[];
  dealerBenchmarkColumns: IDefaultTableColumnConfig[];
  kpiCardConfig: IWebsiteOverviewKpiConfig;
  chartSeriesColors: string[];
  referrerTypeColors?: string[];
  deviceTypeColors: string[];
  referrerQualityColors: string[];
  showReferrerQualityRate?: boolean;
  showReferrerTypeRateMetric?: boolean;
  benchmarkTitle: string;
  benchmarkCardHeader: ICardHeaderConfig;
  referrerQualityCardHeader: ICardHeaderConfig;
  referrerTypeCardHeader: ICardHeaderConfig;
  trafficByDeviceTypeHeader: ICardHeaderConfig;
  visitorTrendCardHeader: ICardHeaderConfig;
  kpiSheetOptions?: Record<KpiSheetOptions, KpiSheetColumn[]>,
  reverseBounceRateTrendArrow?: boolean;
  showPieChart: boolean;
  metricDisplayModes?: INameValuePair[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IHospitalityWebsiteOverviewConfig {
  reportTitle: string;
  useNewMetricCutoffDate: boolean;
  useDefaultComparisonColumns: boolean;
  newMetricCutoffDate?: Date;
  billboards: IWebsiteOverviewBillboardConfig[];
  dealerBenchmarkColumns: IDefaultTableColumnConfig[];
  kpiCardConfig: IWebsiteOverviewKpiConfig;
  chartSeriesColors: string[];
  referrerTypeColors?: string[];
  deviceTypeColors: string[];
  referrerQualityColors: string[];
  showReferrerQualityRate?: boolean;
  showReferrerTypeRateMetric?: boolean;
  benchmarkTitle: string;
  benchmarkCardHeader: ICardHeaderConfig;
  referrerQualityCardHeader: ICardHeaderConfig;
  analysisVisualizationHeaderConfig?: IAnalysisVisualizationHeaderConfig;
  referrerTypeCardHeader?: ICardHeaderConfig;
  trafficByDeviceTypeHeader: ICardHeaderConfig;
  visitorTrendCardHeader: ICardHeaderConfig;
  kpiSheetOptions?: Record<KpiSheetOptions, KpiSheetColumn[]>,
  reverseBounceRateTrendArrow?: boolean;
  showPieChart: boolean;
  metricDisplayModes?: INameValuePair[];
  calendarColors?: string[];
}

export interface IAnalysisVisualizationConfig {
  defaultPropertyKey?: string,
  metricCharts: IAnalysisVisualizationCharts[],
  seriesColors: string[]
}

export interface IAnalysisVisualizationCharts {
  propertyKey: string,
  chartOptions: Options
}

export interface IAnalysisVisualizationHeaderConfig {
  visualizationOptions: IVisualizationOptions;
}

export interface IMetricSelectChangeConfig {
  metricSelect: IMetricConfig,
  currentPersective: IVisualizationPerspective
}

export interface IVisualizationOptions {
  defaultPerspective: string;
  perspectives: IVisualizationPerspective[]
}

export interface IVisualizationExpandoConfig {
  enabled: boolean;
}

export interface IVisualizationMetricSelectConfig {
  enabled: boolean;
  //selectedMetricId?: number;
  metricSelectConfig?: IMetricSelectConfig;
}

export interface IVisualizationHelpConfig {
  enabled: boolean;
  helpTextKey: string;
  helpTextTitle: string;
}

export interface IDashboardConfig {
  dashboards: IDashboard[];
}

export interface IDashboard {
  displayName: string;
  iconClass: string
  dashboardSections: IDashboardSectionConfig [];
}

export interface IDashboardSectionConfig {
  sectionTypes: string;
}

export interface IMetricSection {
  id: number;
  displayName: string;
  propertyName: string;
  metricFormatKey: string;
  trendArrowDrivingPropertyName: string;
  showTrendArrow: boolean;
}

export interface IChartSection {
  chartType: string;
}


export interface IDataSliceConfig {
  enabled: boolean;
  sliceOptions: INameValuePair[];
}

export interface IVisualizationPerspective {
  code: string;
  type: string;
  displayName: string;
  iconClass: string;
  helpConfig: IVisualizationHelpConfig;
  expando: IVisualizationExpandoConfig;
  metricSelect: IVisualizationMetricSelectConfig;
  dataSliceConfig: IDataSliceConfig;
  metricDisplayModes?: IMetricDisplayModes;
  dataTableObject?: any;
}

export interface IMetricDisplayModes {
  enabled: boolean;
  metricDisplayModes: INameValuePair[];
}

export interface IWebsiteProviderConfig {
  reportTitle: string;
  useDefaultComparisonColumns?: boolean;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  metricDisplayModes?: INameValuePair[];
}

export interface IWebsiteProviderScorecardConfig {
  reportTitle: string;
  scorecardMetrics: IWebsiteProviderScorecardBillboardConfig[];
}

export interface IWebsiteProviderScorecardBillboardConfig {
  metric: IMetricType;
  metricPosition: number;
  filterPosition: number;
  metricFormatKey: string;
}

export interface IWebsiteReferrerConfig {
  reportTitle: string;
  useDefaultComparisonColumns?: boolean;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  metricDisplayModes?: INameValuePair[];
}

export interface IWebsiteScorecardConfig {
  reportTitle: string;
  scorecards: IWebsiteScorecardCategoryConfig[];
  previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
  useChevronTrendIcon?: boolean;
}

export interface IWebsiteSummaryConfig {
  reportTitle: string;
  useDefaultComparisonColumns?: boolean;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  trendTableMetrics: INameValuePair[];
  metricDisplayModes?: INameValuePair[];
  tableHeader?: string;
  // previousDateColumnDisplayOverrides?: IPreviousDateRangeDisplay;
}

export interface IHospitalityWebsiteSummaryConfig {
  reportTitle: string;
  useDefaultComparisonColumns: boolean;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  trendTableMetricKeys: string[];
  metricDisplayModes?: INameValuePair[];
}

export interface IWebsiteVehicleConfig {
  reportTitle: string;
  benchmarkCardHeader: ICardHeaderConfig;
  graphConfig: IMultiLevelDualAxisLineGraphConfig;
  summaryTableColumns: IDefaultTableColumnConfig[];
  metricDisplayModes?: INameValuePair[];
}

export interface IResourceDownloadConfig {
  label: string;
  imgUrl: string;
  downloadUrl: string;
}

export interface IDealerResourceCategoryConfig{
  category: string;
  resourceConfig: IResourceDownloadConfig[];
}

export interface IPanelOverviewConfig {
  panels: IPanelConfig[];
}

export interface IPanelConfig {
  name: string;
  title: string;
  titleLinkUrl: string;
  titleLinkUrlFilters: any[];
  type: string;
  elements: Element[];
  metricSelect: IVisualizationMetricSelectConfig;
}

export interface IDataPanelConfing extends IPanelConfig {
  defaultIndexCode: string;
  indexes: IIndexConfig[];
}

// export interface IElementType {
//   name: string;
//   elementType: ElementType;
// }

export type ElementType = 'stacked-two-kpi-metric' | 'kpi-spark-area-fill' | 'kpi-bar-chart' | 'kpi-metric' | 'kpi-stacked-bar';

export interface IKpiMetricElement{
  metricName: string;
  trendArrowMetricName: string;
}

export interface IStackedTwoKpiElement{
  metricConfigs: IKpiMetricElement[];
}

export interface IKpiSparkAreaFillElement{
  trendMetric: string;
}

export interface IKpiBarChartElement{
  metricName: string;
}

export interface IIndexConfig {
  code: string;
  label: string;
  metricSets: IMetricSet[];
  dataSet: string;
  settings: IMetricSettings[];
}

export interface IMetricSettings {
  metricName: string;
  metricSets: IMetricSet[];
}

export interface IMetricSet {
  code: string;
  label: string;
}
